<script setup>
import { computed, h } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import Table from 'dashboard/components/table/Table.vue';
import SummaryArchiveLink from './SummaryArchiveLink.vue';
import { formatReportDuration, durationMetricTooltip } from '../constants';
import { TIME_CELL_METRIC_MAP } from '../helpers/summaryReportArchiveHelper';

const props = defineProps({
  metrics: {
    type: Object,
    default: () => ({}),
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const columnHelper = createColumnHelper();

const formatCount = value => (value ? Number(value).toLocaleString() : '--');
const formatRate = value =>
  value === null || value === undefined || value === ''
    ? '--'
    : Number(value).toFixed(1);
const formatCsat = (average, responses) => {
  if (average === null || average === undefined || average === '') return '--';
  return `${Number(average).toFixed(1)}/${responses || 0}`;
};

const row = computed(() => {
  const m = props.metrics || {};
  return {
    id: m.id,
    name: m.name,
    type: 'agent',
    uniqueCyclesHandled: formatCount(m.uniqueCyclesHandled),
    assignmentEpisodesCount: formatCount(m.assignmentEpisodesCount),
    answeredEpisodesCount: formatCount(m.answeredEpisodesCount),
    unansweredEpisodesCount: formatCount(m.unansweredEpisodesCount),
    resolutionsCount: formatCount(m.resolvedConversationsCount),
    transfersInCount: formatCount(m.transfersInCount),
    transfersOutCount: formatCount(m.transfersOutCount),
    avgFirstResponseTime: formatReportDuration(m.avgFirstResponseTime),
    medianFirstResponseTime: formatReportDuration(m.medianFirstResponseTime),
    avgOnlineFirstResponseTime: formatReportDuration(
      m.avgOnlineFirstResponseTime
    ),
    medianOnlineFirstResponseTime: formatReportDuration(
      m.medianOnlineFirstResponseTime
    ),
    avgReplyTime: formatReportDuration(m.avgReplyTime),
    medianAssignmentDuration: formatReportDuration(m.medianAssignmentDuration),
    onlineSeconds: formatReportDuration(m.onlineSeconds),
    resolvedPerOnlineHour: formatRate(m.resolvedPerOnlineHour),
    csat: formatCsat(m.csatAverage, m.csatResponses),
    sourceMetrics: m,
  };
});

const headerLabel = key => {
  const labels = {
    UNIQUE_CYCLES: t('SUMMARY_REPORTS.UNIQUE_CYCLES'),
    ACCEPTED_EPISODES: t('SUMMARY_REPORTS.ACCEPTED_EPISODES'),
    ANSWERED_EPISODES: t('SUMMARY_REPORTS.ANSWERED_EPISODES'),
    UNANSWERED_EPISODES: t('SUMMARY_REPORTS.UNANSWERED_EPISODES'),
    RESOLUTION_COUNT: t('SUMMARY_REPORTS.RESOLUTION_COUNT'),
    TRANSFERS_IN: t('SUMMARY_REPORTS.TRANSFERS_IN'),
    TRANSFERS_OUT: t('SUMMARY_REPORTS.TRANSFERS_OUT'),
    AVG_FIRST_RESPONSE_TIME: t('SUMMARY_REPORTS.AVG_FIRST_RESPONSE_TIME'),
    MEDIAN_FIRST_RESPONSE_TIME: t('SUMMARY_REPORTS.MEDIAN_FIRST_RESPONSE_TIME'),
    AVG_ONLINE_FRT: t('SUMMARY_REPORTS.AVG_ONLINE_FRT'),
    MEDIAN_ONLINE_FRT: t('SUMMARY_REPORTS.MEDIAN_ONLINE_FRT'),
    AVG_REPLY_TIME: t('SUMMARY_REPORTS.AVG_REPLY_TIME'),
    MEDIAN_ASSIGNMENT_DURATION: t('SUMMARY_REPORTS.MEDIAN_ASSIGNMENT_DURATION'),
    ONLINE_TIME: t('SUMMARY_REPORTS.ONLINE_TIME'),
    RESOLVED_PER_ONLINE_HOUR: t('SUMMARY_REPORTS.RESOLVED_PER_ONLINE_HOUR'),
    CSAT: t('SUMMARY_REPORTS.CSAT'),
  };
  return labels[key] || key;
};

const headerShortLabel = key => {
  const labels = {
    UNIQUE_CYCLES: t('SUMMARY_REPORTS.HEADER_SHORT.UNIQUE_CYCLES'),
    ACCEPTED_EPISODES: t('SUMMARY_REPORTS.HEADER_SHORT.ACCEPTED_EPISODES'),
    ANSWERED_EPISODES: t('SUMMARY_REPORTS.HEADER_SHORT.ANSWERED_EPISODES'),
    UNANSWERED_EPISODES: t('SUMMARY_REPORTS.HEADER_SHORT.UNANSWERED_EPISODES'),
    RESOLUTION_COUNT: t('SUMMARY_REPORTS.HEADER_SHORT.RESOLUTION_COUNT'),
    TRANSFERS_IN: t('SUMMARY_REPORTS.HEADER_SHORT.TRANSFERS_IN'),
    TRANSFERS_OUT: t('SUMMARY_REPORTS.HEADER_SHORT.TRANSFERS_OUT'),
    AVG_FIRST_RESPONSE_TIME: t(
      'SUMMARY_REPORTS.HEADER_SHORT.AVG_FIRST_RESPONSE_TIME'
    ),
    MEDIAN_FIRST_RESPONSE_TIME: t(
      'SUMMARY_REPORTS.HEADER_SHORT.MEDIAN_FIRST_RESPONSE_TIME'
    ),
    AVG_ONLINE_FRT: t('SUMMARY_REPORTS.HEADER_SHORT.AVG_ONLINE_FRT'),
    MEDIAN_ONLINE_FRT: t('SUMMARY_REPORTS.HEADER_SHORT.MEDIAN_ONLINE_FRT'),
    AVG_REPLY_TIME: t('SUMMARY_REPORTS.HEADER_SHORT.AVG_REPLY_TIME'),
    MEDIAN_ASSIGNMENT_DURATION: t(
      'SUMMARY_REPORTS.HEADER_SHORT.MEDIAN_ASSIGNMENT_DURATION'
    ),
    ONLINE_TIME: t('SUMMARY_REPORTS.HEADER_SHORT.ONLINE_TIME'),
    RESOLVED_PER_ONLINE_HOUR: t(
      'SUMMARY_REPORTS.HEADER_SHORT.RESOLVED_PER_ONLINE_HOUR'
    ),
    CSAT: t('SUMMARY_REPORTS.HEADER_SHORT.CSAT'),
  };
  return labels[key] || headerLabel(key);
};

const rawMetricKey = accessor =>
  accessor === 'resolutionsCount' ? 'resolvedConversationsCount' : accessor;

const countCell = accessor => cellProps => {
  const metric = cellProps.row.original.sourceMetrics || {};
  return h(SummaryArchiveLink, {
    value: cellProps.row.original[accessor],
    metricKey: accessor,
    row: cellProps.row.original,
    archiveContext: props.archiveContext,
    rawValue: metric[rawMetricKey(accessor)],
  });
};

const timeCell = accessor => cellProps => {
  const value = cellProps.row.original[accessor];
  const seconds = cellProps.row.original.sourceMetrics?.[accessor];
  const definition = cellProps.column.columnDef.meta?.headerTitle;
  const metricKey = TIME_CELL_METRIC_MAP[accessor];
  if (!value || value === '--') {
    return h('span', { class: 'text-n-slate-11' }, '--');
  }
  if (metricKey) {
    return h(SummaryArchiveLink, {
      value,
      metricKey,
      row: cellProps.row.original,
      archiveContext: props.archiveContext,
      rawValue: 1,
      title: durationMetricTooltip(t, definition, seconds),
    });
  }
  return h(
    'span',
    {
      class: 'tabular-nums',
      title: durationMetricTooltip(t, definition, seconds),
    },
    value
  );
};

const metricColumn = (accessor, headerKey, cell) =>
  columnHelper.accessor(accessor, {
    header: headerShortLabel(headerKey),
    cell,
    meta: { metric: true, headerTitle: headerLabel(headerKey) },
  });

const performanceColumns = computed(() => [
  metricColumn(
    'uniqueCyclesHandled',
    'UNIQUE_CYCLES',
    countCell('uniqueCyclesHandled')
  ),
  metricColumn(
    'assignmentEpisodesCount',
    'ACCEPTED_EPISODES',
    countCell('assignmentEpisodesCount')
  ),
  metricColumn(
    'answeredEpisodesCount',
    'ANSWERED_EPISODES',
    countCell('answeredEpisodesCount')
  ),
  metricColumn(
    'unansweredEpisodesCount',
    'UNANSWERED_EPISODES',
    countCell('unansweredEpisodesCount')
  ),
  metricColumn(
    'resolutionsCount',
    'RESOLUTION_COUNT',
    countCell('resolutionsCount')
  ),
  metricColumn(
    'transfersInCount',
    'TRANSFERS_IN',
    countCell('transfersInCount')
  ),
  metricColumn(
    'transfersOutCount',
    'TRANSFERS_OUT',
    countCell('transfersOutCount')
  ),
]);

const qualityColumns = computed(() => [
  metricColumn(
    'avgFirstResponseTime',
    'AVG_FIRST_RESPONSE_TIME',
    timeCell('avgFirstResponseTime')
  ),
  metricColumn(
    'medianFirstResponseTime',
    'MEDIAN_FIRST_RESPONSE_TIME',
    timeCell('medianFirstResponseTime')
  ),
  metricColumn(
    'avgOnlineFirstResponseTime',
    'AVG_ONLINE_FRT',
    timeCell('avgOnlineFirstResponseTime')
  ),
  metricColumn(
    'medianOnlineFirstResponseTime',
    'MEDIAN_ONLINE_FRT',
    timeCell('medianOnlineFirstResponseTime')
  ),
  metricColumn('avgReplyTime', 'AVG_REPLY_TIME', timeCell('avgReplyTime')),
  metricColumn(
    'medianAssignmentDuration',
    'MEDIAN_ASSIGNMENT_DURATION',
    timeCell('medianAssignmentDuration')
  ),
  metricColumn('onlineSeconds', 'ONLINE_TIME', timeCell('onlineSeconds')),
  metricColumn('resolvedPerOnlineHour', 'RESOLVED_PER_ONLINE_HOUR', cellProps =>
    h(
      'span',
      { class: 'tabular-nums' },
      cellProps.row.original.resolvedPerOnlineHour
    )
  ),
  metricColumn('csat', 'CSAT', cellProps => {
    const metric = cellProps.row.original.sourceMetrics || {};
    return h(SummaryArchiveLink, {
      value: cellProps.row.original.csat,
      metricKey: 'csatResponses',
      row: cellProps.row.original,
      archiveContext: props.archiveContext,
      rawValue: metric.csatResponses,
    });
  }),
]);

const tableRows = computed(() => (props.metrics?.id ? [row.value] : []));

const performanceTable = useVueTable({
  get data() {
    return tableRows.value;
  },
  get columns() {
    return performanceColumns.value;
  },
  getCoreRowModel: getCoreRowModel(),
});

const qualityTable = useVueTable({
  get data() {
    return tableRows.value;
  },
  get columns() {
    return qualityColumns.value;
  },
  getCoreRowModel: getCoreRowModel(),
});
</script>

<template>
  <div>
    <div v-if="tableRows.length" class="flex flex-col gap-5">
      <section
        class="relative flex-1 px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <h3 class="mb-3 px-2 text-base font-medium text-n-slate-12">
          {{ $t('SUMMARY_REPORTS.TABLES.AGENT_PERFORMANCE') }}
        </h3>
        <Table :table="performanceTable" type="summary" />
      </section>
      <section
        class="relative flex-1 px-2 py-2 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
      >
        <h3 class="mb-3 px-2 text-base font-medium text-n-slate-12">
          {{ $t('SUMMARY_REPORTS.TABLES.AGENT_TIME_QUALITY') }}
        </h3>
        <Table :table="qualityTable" type="summary" />
      </section>
    </div>
  </div>
</template>
